/*
  Urban Customz — Bundle Configurator
  -----------------------------------
  One state object, rendered into the shell markup in snippets/bundle-configurator.liquid,
  fed by the JSON config emitted by sections/bundle-product.liquid.

  Money rule: Shopify is the source of truth. The bundle is one line at its fixed price;
  every paid upgrade is a real variant of the hidden add-on product, added as its own line
  with quantity = pieces it applies to. All lines share `_bundle_id`. After adding, we
  re-read /cart.js and refuse to continue unless the group's subtotal equals the total the
  customer was shown.
*/
(function () {
  'use strict';

  /* ---------------------------------------------------------------- helpers */

  var SIZE_ABBR = {
    'x-small': 'XS', 'xsmall': 'XS', 'xs': 'XS',
    'small': 'S', 's': 'S',
    'medium': 'M', 'm': 'M',
    'large': 'L', 'l': 'L',
    'x-large': 'XL', 'xlarge': 'XL', 'xl': 'XL',
    'xx-large': '2XL', 'xxl': '2XL', '2xl': '2XL',
    'xxx-large': '3XL', 'xxxl': '3XL', '3xl': '3XL',
    '4xl': '4XL', '5xl': '5XL', '6xl': '6XL',
    'os': 'One size', 'one size': 'One size', 'osfa': 'One size'
  };

  function sizeLabel(v) {
    if (!v) return '';
    var k = String(v).trim().toLowerCase();
    return SIZE_ABBR[k] || v;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents, format) {
    cents = Math.round(Number(cents) || 0);
    var fmt = format || '${{amount}}';
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = m ? m[1] : 'amount';
    function num(n, precision, thousands, decimal) {
      var s = (n / 100).toFixed(precision);
      var parts = s.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }
    var value;
    switch (key) {
      case 'amount_no_decimals': value = num(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = num(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = num(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = num(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = num(cents, 0, ' ', '.'); break;
      case 'amount_with_space_separator': value = num(cents, 2, ' ', ','); break;
      default: value = num(cents, 2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]+>/g, '');
  }

  function uid() {
    return 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  function isImageName(name) {
    return /\.(png|jpe?g|gif|webp|svg)$/i.test(name || '');
  }

  function fileExt(name) {
    var p = String(name || '').split('.');
    return p.length > 1 ? p.pop().toLowerCase() : '';
  }

  var ALLOWED_EXT = ['png', 'jpg', 'jpeg', 'pdf', 'psd', 'tif', 'tiff', 'svg', 'ai', 'eps', 'webp'];

  /* -------------------------------------------------------------- uploads */

  function uploadToUploadcare(file, cfg, onProgress) {
    return new Promise(function (resolve, reject) {
      if (!cfg.publicKey) { reject(new Error('Uploads are not configured. Please contact us.')); return; }
      var fd = new FormData();
      fd.append('UPLOADCARE_PUB_KEY', cfg.publicKey);
      fd.append('UPLOADCARE_STORE', '1');
      fd.append('file', file, file.name);
      var xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://upload.uploadcare.com/base/');
      xhr.upload.onprogress = function (e) {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            var data = JSON.parse(xhr.responseText);
            var id = data.file || (data.files && data.files[0]);
            if (!id) throw new Error('no id');
            var base = cfg.cdnBase || 'https://ucarecdn.com/';
            if (base.slice(-1) !== '/') base += '/';
            var safeName = String(file.name).replace(/[^\w.\-]+/g, '_');
            resolve({
              url: base + id + '/' + safeName,
              uuid: id,
              name: file.name,
              size: file.size,
              isImage: isImageName(file.name)
            });
          } catch (e) {
            reject(new Error('Upload succeeded but the response was unexpected.'));
          }
        } else {
          reject(new Error('Upload failed (' + xhr.status + '). Please try again.'));
        }
      };
      xhr.onerror = function () { reject(new Error('Network error during upload. Please try again.')); };
      xhr.send(fd);
    });
  }

  function previewFor(upload) {
    if (!upload) return '';
    if (upload.isImage && /ucarecdn|ucarecd/.test(upload.url)) {
      // Uploadcare image transformation — works for raster and svg
      return upload.url.replace(/\/[^/]*$/, '/') + '-/preview/480x480/-/format/auto/';
    }
    return upload.isImage ? upload.url : '';
  }

  /* --------------------------------------------------------- configurator */

  function Configurator(root, config, opts) {
    this.root = root;
    this.config = config;
    this.opts = opts || {};
    this.els = {
      dialog: root.querySelector('.bc__dialog'),
      body: root.querySelector('[data-bc-body]'),
      summary: root.querySelector('[data-bc-summary]'),
      footer: root.querySelector('[data-bc-footer]'),
      tabs: root.querySelector('[data-bc-tabs]'),
      progress: root.querySelector('[data-bc-progress]'),
      title: root.querySelector('[data-bc-title]'),
      kicker: root.querySelector('[data-bc-kicker]')
    };
    this.storageKey = 'uc-bundle:' + config.product.id;
    this.steps = this.buildSteps();
    this.state = this.restore() || this.freshState();
    this.uploading = {}; // key -> progress 0..1
    this.errors = {};    // stepIndex -> message
    this.busy = false;
    this.bind();
  }

  Configurator.prototype.buildSteps = function () {
    var steps = [{ type: 'artwork', label: 'Logo' }];
    var cfg = this.config;
    cfg.steps.forEach(function (s) {
      var opts = s.options || [];
      var colorOpt = null, sizeOpt = null;
      opts.forEach(function (o) {
        var n = o.name.toLowerCase();
        if (!colorOpt && (n.indexOf('color') > -1 || n.indexOf('colour') > -1)) colorOpt = o;
        else if (!sizeOpt && n.indexOf('size') > -1) sizeOpt = o;
      });
      // Fallbacks: if no colour option, use the first option as the "colour" axis
      if (!colorOpt && !s.product.hasOnlyDefaultVariant && opts.length) {
        colorOpt = opts.filter(function (o) { return o !== sizeOpt; })[0] || null;
      }
      // Products with a second non-size option (e.g. tote bags: Colour + Type) use it as the quantity axis
      if (!sizeOpt && opts.length > 1) {
        sizeOpt = opts.filter(function (o) { return o !== colorOpt; })[0] || null;
      }
      var variantIndex = {};
      var colorImages = {};
      var colorsInStock = {};
      s.variants.forEach(function (v) {
        var c = colorOpt ? v['option' + colorOpt.position] : '__default';
        var z = sizeOpt ? v['option' + sizeOpt.position] : '__default';
        variantIndex[c + '||' + z] = v;
        if (v.available) colorsInStock[c] = true;
        if (!colorImages[c] && v.image) colorImages[c] = v.image;
      });
      steps.push({
        type: 'garment',
        label: s.shortTitle || s.product.title,
        step: s,
        colorOpt: colorOpt,
        sizeOpt: sizeOpt,
        variantIndex: variantIndex,
        colorImages: colorImages,
        colorsInStock: colorsInStock
      });
    });
    steps.push({ type: 'review', label: 'Review' });
    return steps;
  };

  Configurator.prototype.freshState = function () {
    var st = { v: 1, bundleId: uid(), current: 0, artwork: null, garments: {}, notes: '', maxVisited: 0 };
    this.config.steps.forEach(function (s) {
      st.garments[s.key] = { colors: [], addons: {} };
      (s.addons || []).forEach(function (a) { st.garments[s.key].addons[a.key] = { on: false, mode: 'main', artwork: null }; });
    });
    return st;
  };

  Configurator.prototype.restore = function () {
    try {
      var raw = localStorage.getItem(this.storageKey);
      if (!raw) return null;
      var st = JSON.parse(raw);
      if (!st || st.v !== 1 || !st.garments) return null;
      // Make sure every configured step/addon exists in the saved state
      var self = this;
      this.config.steps.forEach(function (s) {
        st.garments[s.key] = st.garments[s.key] || { colors: [], addons: {} };
        st.garments[s.key].addons = st.garments[s.key].addons || {};
        (s.addons || []).forEach(function (a) {
          st.garments[s.key].addons[a.key] = st.garments[s.key].addons[a.key] || { on: false, mode: 'main', artwork: null };
        });
      });
      if (typeof st.current !== 'number' || st.current < 0 || st.current >= self.steps.length) st.current = 0;
      return st;
    } catch (e) { return null; }
  };

  Configurator.prototype.persist = function () {
    try { localStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch (e) { /* private mode */ }
  };

  Configurator.prototype.clearSaved = function () {
    try { localStorage.removeItem(this.storageKey); } catch (e) { /* noop */ }
  };

  /* --------------------------------------------------------------- open/close */

  Configurator.prototype.open = function (trigger) {
    this.trigger = trigger || document.activeElement;
    this.root.hidden = false;
    document.documentElement.classList.add('bc-open');
    this.renderAll();
    var self = this;
    // setTimeout rather than requestAnimationFrame: rAF is paused in background tabs
    setTimeout(function () {
      self.root.classList.add('is-open');
      self.els.dialog.focus();
    }, 20);
  };

  Configurator.prototype.close = function () {
    var self = this;
    this.persist();
    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('bc-open');
    setTimeout(function () { self.root.hidden = true; }, 220);
    if (this.trigger && this.trigger.focus) this.trigger.focus();
  };

  Configurator.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-bc-close]');
      if (t) { e.preventDefault(); self.close(); return; }
      var act = e.target.closest('[data-act]');
      if (act) { self.onAction(act, e); }
    });
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); self.close(); return; }
      if (e.key === 'Tab') self.trapFocus(e);
    });
    this.root.addEventListener('input', function (e) { self.onInput(e); });
    this.root.addEventListener('change', function (e) { self.onChange(e); });
    // drag & drop uploads
    this.root.addEventListener('dragover', function (e) {
      var dz = e.target.closest('[data-dropzone]');
      if (dz) { e.preventDefault(); dz.classList.add('is-over'); }
    });
    this.root.addEventListener('dragleave', function (e) {
      var dz = e.target.closest('[data-dropzone]');
      if (dz) dz.classList.remove('is-over');
    });
    this.root.addEventListener('drop', function (e) {
      var dz = e.target.closest('[data-dropzone]');
      if (!dz) return;
      e.preventDefault(); dz.classList.remove('is-over');
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) self.handleUpload(dz.getAttribute('data-dropzone'), f);
    });
  };

  Configurator.prototype.trapFocus = function (e) {
    var f = this.els.dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /* --------------------------------------------------------------- state ops */

  Configurator.prototype.garmentTotals = function (stepDef) {
    var g = this.state.garments[stepDef.step.key];
    var total = 0;
    g.colors.forEach(function (c) {
      Object.keys(c.qty).forEach(function (k) { total += Number(c.qty[k]) || 0; });
    });
    return { assigned: total, required: stepDef.step.quantity, remaining: Math.max(0, stepDef.step.quantity - total) };
  };

  Configurator.prototype.stepComplete = function (i) {
    var s = this.steps[i];
    if (s.type === 'artwork') return !!(this.state.artwork && this.state.artwork.url);
    if (s.type === 'garment') {
      var t = this.garmentTotals(s);
      if (t.assigned !== t.required) return false;
      var g = this.state.garments[s.step.key];
      var ok = true;
      Object.keys(g.addons).forEach(function (k) {
        var a = g.addons[k];
        if (a.on && a.mode === 'custom' && !(a.artwork && a.artwork.url)) ok = false;
      });
      return ok;
    }
    if (s.type === 'review') return this.allComplete();
    return false;
  };

  Configurator.prototype.allComplete = function () {
    for (var i = 0; i < this.steps.length - 1; i++) if (!this.stepComplete(i)) return false;
    return true;
  };

  Configurator.prototype.stepError = function (i) {
    var s = this.steps[i];
    if (s.type === 'artwork') return this.state.artwork && this.state.artwork.url ? '' : 'Upload your logo to continue.';
    if (s.type === 'garment') {
      var t = this.garmentTotals(s);
      if (t.assigned < t.required) return 'Add ' + t.remaining + ' more ' + (t.remaining === 1 ? 'piece' : 'pieces') + ' to reach ' + t.required + '.';
      if (t.assigned > t.required) return 'You have ' + (t.assigned - t.required) + ' too many. Reduce to ' + t.required + '.';
      var g = this.state.garments[s.step.key];
      var missing = '';
      var self = this;
      Object.keys(g.addons).forEach(function (k) {
        var a = g.addons[k];
        if (a.on && a.mode === 'custom' && !(a.artwork && a.artwork.url) && !missing) {
          var def = self.addonDef(s, k);
          missing = 'Upload artwork for the ' + (def ? def.title.toLowerCase() : 'print') + ' or choose "Use my main logo".';
        }
      });
      return missing;
    }
    return '';
  };

  Configurator.prototype.addonDef = function (stepDef, key) {
    return (stepDef.step.addons || []).filter(function (a) { return a.key === key; })[0] || null;
  };

  /* Size surcharge: sizes whose variant costs more than the garment's base price
     (typically 2XL and up) are charged once per piece via the add-on product's
     surcharge variant, mirroring the store's regular per-size pricing. */
  Configurator.prototype.surchargeUnit = function (stepDef, v) {
    var sc = this.config.sizeSurcharge;
    if (!sc || !v || typeof v.priceCents !== 'number') return 0;
    var base = stepDef.step.product.basePriceCents;
    if (typeof base !== 'number') return 0;
    return v.priceCents > base ? sc.priceCents : 0;
  };

  Configurator.prototype.surchargePieces = function (stepDef) {
    var self = this;
    var g = this.state.garments[stepDef.step.key];
    var n = 0;
    g.colors.forEach(function (c) {
      Object.keys(c.qty).forEach(function (z) {
        var q = Number(c.qty[z]) || 0;
        if (q > 0 && self.surchargeUnit(stepDef, stepDef.variantIndex[c.color + '||' + z]) > 0) n += q;
      });
    });
    return n;
  };

  Configurator.prototype.stepHasSurcharge = function (stepDef) {
    var keys = Object.keys(stepDef.variantIndex);
    for (var i = 0; i < keys.length; i++) if (this.surchargeUnit(stepDef, stepDef.variantIndex[keys[i]]) > 0) return true;
    return false;
  };

  Configurator.prototype.surchargeLine = function (stepDef) {
    var sc = this.config.sizeSurcharge;
    if (!sc) return null;
    var n = this.surchargePieces(stepDef);
    if (!n) return null;
    return {
      stepKey: stepDef.step.key,
      stepLabel: stepDef.label,
      addon: { key: '__surcharge', title: sc.title, variantId: sc.variantId, priceCents: sc.priceCents },
      pieces: n,
      total: sc.priceCents * n,
      artwork: null,
      surcharge: true
    };
  };

  Configurator.prototype.totals = function () {
    var self = this;
    var lines = [];
    var addonsCents = 0;
    this.steps.forEach(function (s) {
      if (s.type !== 'garment') return;
      var g = self.state.garments[s.step.key];
      var pieces = self.garmentTotals(s).assigned || s.step.quantity;
      (s.step.addons || []).forEach(function (a) {
        var st = g.addons[a.key];
        if (st && st.on) {
          var total = a.priceCents * pieces;
          addonsCents += total;
          lines.push({ stepKey: s.step.key, stepLabel: s.label, addon: a, pieces: pieces, total: total, artwork: st.mode === 'custom' ? st.artwork : null });
        }
      });
      var sl = self.surchargeLine(s);
      if (sl) { addonsCents += sl.total; lines.push(sl); }
    });
    return { base: this.config.product.priceCents, addons: lines, addonsCents: addonsCents, total: this.config.product.priceCents + addonsCents };
  };

  Configurator.prototype.goTo = function (i, opts) {
    opts = opts || {};
    if (i < 0 || i >= this.steps.length) return;
    // Moving forward requires the current step to be valid
    if (i > this.state.current && !opts.force) {
      for (var k = this.state.current; k < i; k++) {
        if (!this.stepComplete(k)) {
          this.errors[k] = this.stepError(k);
          this.state.current = k;
          this.renderAll();
          this.focusError();
          return;
        }
      }
    }
    this.state.current = i;
    this.state.maxVisited = Math.max(this.state.maxVisited || 0, i);
    this.persist();
    this.renderAll();
    this.els.body.scrollTop = 0;
    var h = this.els.body.querySelector('h3');
    if (h) h.setAttribute('tabindex', '-1'), h.focus({ preventScroll: true });
  };

  Configurator.prototype.focusError = function () {
    var el = this.els.body.querySelector('.bc__error');
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  /* -------------------------------------------------------------- rendering */

  Configurator.prototype.renderAll = function () {
    this.renderHeader();
    this.renderBody();
    this.renderSummary();
    this.renderFooter();
  };

  Configurator.prototype.renderHeader = function () {
    var self = this;
    var cur = this.state.current;
    var s = this.steps[cur];
    var titles = {
      artwork: 'Upload your logo',
      garment: s.type === 'garment' ? 'Choose your ' + s.step.quantity + ' ' + s.label.toLowerCase() : '',
      review: 'Review and add to cart'
    };
    this.els.kicker.textContent = this.config.product.title + ' · Step ' + (cur + 1) + ' of ' + this.steps.length;
    this.els.title.textContent = titles[s.type];
    this.els.tabs.innerHTML = this.steps.map(function (st, i) {
      var done = i < self.steps.length - 1 && self.stepComplete(i);
      var reachable = i <= (self.state.maxVisited || 0) + 1;
      var cls = 'bc__tab' + (i === cur ? ' is-current' : '') + (done ? ' is-done' : '') + (!reachable ? ' is-locked' : '');
      return '<button type="button" class="' + cls + '" data-act="goto" data-i="' + i + '" ' + (reachable ? '' : 'disabled') + ' aria-current="' + (i === cur ? 'step' : 'false') + '">' +
        '<span class="bc__tab-num">' + (done ? '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6.5l2.3 2.3 4.7-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' : (i + 1)) + '</span>' +
        '<span class="bc__tab-label">' + esc(st.label) + '</span></button>';
    }).join('');
    this.els.progress.style.width = Math.round(((cur + 1) / this.steps.length) * 100) + '%';
  };

  Configurator.prototype.renderBody = function () {
    var s = this.steps[this.state.current];
    var html = '';
    if (s.type === 'artwork') html = this.viewArtwork();
    else if (s.type === 'garment') html = this.viewGarment(s);
    else html = this.viewReview();
    this.els.body.innerHTML = html;
  };

  Configurator.prototype.uploadCard = function (key, upload, opts) {
    opts = opts || {};
    var prog = this.uploading[key];
    var maxMb = this.config.upload.maxMb || 25;
    if (typeof prog === 'number') {
      return '<div class="bc__upload is-busy"><div class="bc__upload-bar"><span style="width:' + Math.round(prog * 100) + '%"></span></div><p>Uploading… ' + Math.round(prog * 100) + '%</p></div>';
    }
    if (upload && upload.url) {
      var pv = previewFor(upload);
      return '<div class="bc__upload is-done">' +
        '<div class="bc__upload-preview">' + (pv ? '<img src="' + esc(pv) + '" alt="">' : '<span class="bc__upload-ext">' + esc(fileExt(upload.name).toUpperCase() || 'FILE') + '</span>') + '</div>' +
        '<div class="bc__upload-meta"><strong>' + esc(upload.name) + '</strong><span>' + (upload.size ? (upload.size / 1024 / 1024).toFixed(2) + ' MB · ' : '') + 'Uploaded</span></div>' +
        '<div class="bc__upload-actions"><button type="button" class="bc__link" data-act="replace-upload" data-key="' + esc(key) + '">Replace</button>' +
        (opts.removable ? '<button type="button" class="bc__link bc__link--danger" data-act="remove-upload" data-key="' + esc(key) + '">Remove</button>' : '') + '</div>' +
        '</div>' +
        '<input type="file" class="bc__file" id="bc-file-' + esc(key) + '" data-upload-input="' + esc(key) + '" accept=".png,.jpg,.jpeg,.pdf,.psd,.tif,.tiff,.svg,.ai,.eps,.webp" hidden>';
    }
    return '<label class="bc__dropzone" data-dropzone="' + esc(key) + '" for="bc-file-' + esc(key) + '">' +
      '<span class="bc__dropzone-icon" aria-hidden="true"><svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 16V4m0 0l-4 4m4-4l4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></span>' +
      '<span class="bc__dropzone-title">' + esc(opts.title || 'Upload your logo') + '</span>' +
      '<span class="bc__dropzone-sub">Drag a file here or <u>browse</u>. PNG, JPG, PDF, SVG, AI, EPS, PSD, TIFF · up to ' + maxMb + ' MB</span>' +
      '<input type="file" class="bc__file" id="bc-file-' + esc(key) + '" data-upload-input="' + esc(key) + '" accept=".png,.jpg,.jpeg,.pdf,.psd,.tif,.tiff,.svg,.ai,.eps,.webp" hidden>' +
      '</label>' + (this.uploadErrors && this.uploadErrors[key] ? '<p class="bc__error">' + esc(this.uploadErrors[key]) + '</p>' : '');
  };

  Configurator.prototype.viewArtwork = function () {
    var err = this.errors[this.state.current];
    return '<div class="bc__step">' +
      '<h3 class="bc__h3">Your logo goes on every piece</h3>' +
      '<p class="bc__lead">Upload one file and we will place it on all ' + this.config.totalPieces + ' pieces. Vector files (SVG, AI, PDF) print sharpest; high-resolution PNG or JPG also work.</p>' +
      this.uploadCard('main', this.state.artwork, { title: 'Upload your logo', removable: true }) +
      (err ? '<p class="bc__error" role="alert">' + esc(err) + '</p>' : '') +
      '<div class="bc__tips"><p><strong>Tips</strong></p><ul><li>Transparent background (PNG/SVG) looks best on coloured garments.</li><li>Not sure about your file? Upload what you have — our team checks every order before printing.</li></ul></div>' +
      '</div>';
  };

  Configurator.prototype.viewGarment = function (s) {
    var self = this;
    var g = this.state.garments[s.step.key];
    var t = this.garmentTotals(s);
    var err = this.errors[this.state.current];
    var colorOpt = s.colorOpt, sizeOpt = s.sizeOpt;
    var html = '<div class="bc__step">';

    // product header + counter
    html += '<div class="bc__garment-head">' +
      (s.step.product.image ? '<img class="bc__garment-img" src="' + esc(s.step.product.image) + '" alt="">' : '') +
      '<div><h3 class="bc__h3">' + esc(s.step.quantity + ' ' + s.label) + '</h3><p class="bc__lead">Pick a colour, then set how many of each size. You can add more colours.</p></div>' +
      '<div class="bc__counter ' + (t.assigned === t.required ? 'is-complete' : (t.assigned > t.required ? 'is-over' : '')) + '" data-counter>' +
      '<strong>' + t.assigned + '</strong><span>of ' + t.required + '</span></div>' +
      '</div>';

    // colour chips
    if (colorOpt) {
      var selected = {};
      g.colors.forEach(function (c) { selected[c.color] = true; });
      html += '<div class="bc__section"><div class="bc__section-head"><h4>' + esc(colorOpt.name) + '</h4><span>' + (g.colors.length ? g.colors.length + ' selected' : 'Select one or more') + '</span></div>';
      html += '<div class="bc__chips" role="group" aria-label="' + esc(colorOpt.name) + '">';
      colorOpt.values.forEach(function (val) {
        if (!s.colorsInStock[val]) return;
        var img = s.colorImages[val];
        var on = !!selected[val];
        html += '<button type="button" class="bc__chip' + (on ? ' is-on' : '') + '" data-act="toggle-color" data-color="' + esc(val) + '" aria-pressed="' + on + '" title="' + esc(val) + '">' +
          '<span class="bc__chip-img">' + (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : '<span class="bc__chip-swatch" data-color-name="' + esc(val) + '"></span>') +
          '<span class="bc__chip-check" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.5l2.3 2.3 4.7-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span></span>' +
          '<span class="bc__chip-label">' + esc(val) + '</span></button>';
      });
      html += '</div></div>';
    }

    // size grids per selected colour (or single grid when no colour option)
    var blocks = colorOpt ? g.colors : [{ color: '__default', qty: (g.colors[0] && g.colors[0].qty) || {} }];
    if (!colorOpt && !g.colors.length) { g.colors.push({ color: '__default', qty: {} }); blocks = g.colors; }
    if (blocks.length) {
      var axisLabel = sizeOpt ? (/size/i.test(sizeOpt.name) ? 'Sizes and quantities' : esc(sizeOpt.name) + ' and quantities') : 'Quantity';
      html += '<div class="bc__section"><div class="bc__section-head"><h4>' + axisLabel + '</h4><span>' + (t.remaining ? t.remaining + ' left to assign' : (t.assigned > t.required ? (t.assigned - t.required) + ' over' : 'All assigned')) + '</span></div>';
      blocks.forEach(function (c) {
        var sizes = sizeOpt ? sizeOpt.values.filter(function (z) { var v = s.variantIndex[c.color + '||' + z]; return v && v.available; }) : ['__default'];
        var sub = 0; Object.keys(c.qty).forEach(function (k) { sub += Number(c.qty[k]) || 0; });
        html += '<div class="bc__color-block" data-color-block="' + esc(c.color) + '">';
        if (colorOpt) {
          html += '<div class="bc__color-head">' + (s.colorImages[c.color] ? '<img src="' + esc(s.colorImages[c.color]) + '" alt="">' : '') +
            '<strong>' + esc(c.color) + '</strong><span data-color-sub>' + sub + ' ' + (sub === 1 ? 'piece' : 'pieces') + '</span>' +
            '<button type="button" class="bc__link bc__link--danger" data-act="remove-color" data-color="' + esc(c.color) + '">Remove</button></div>';
        }
        html += '<div class="bc__sizes">';
        sizes.forEach(function (z) {
          var q = Number(c.qty[z]) || 0;
          var extra = self.surchargeUnit(s, s.variantIndex[c.color + '||' + z]);
          html += '<div class="bc__size' + (extra ? ' has-extra' : '') + '">' +
            '<span class="bc__size-label">' + esc(z === '__default' ? 'Quantity' : sizeLabel(z)) + (extra ? '<span class="bc__size-extra">+' + formatMoney(extra, self.config.moneyFormat) + '</span>' : '') + '</span>' +
            '<div class="bc__stepper">' +
            '<button type="button" data-act="qty" data-delta="-1" data-color="' + esc(c.color) + '" data-size="' + esc(z) + '" aria-label="Fewer ' + esc(z) + '"' + (q <= 0 ? ' disabled' : '') + '>−</button>' +
            '<input type="number" inputmode="numeric" min="0" max="' + s.step.quantity + '" value="' + q + '" data-qty data-color="' + esc(c.color) + '" data-size="' + esc(z) + '" aria-label="' + esc(z) + ' quantity">' +
            '<button type="button" data-act="qty" data-delta="1" data-color="' + esc(c.color) + '" data-size="' + esc(z) + '" aria-label="More ' + esc(z) + '"' + (q >= s.step.quantity ? ' disabled' : '') + '>+</button>' +
            '</div></div>';
        });
        html += '</div></div>';
      });
      if (self.config.sizeSurcharge && self.stepHasSurcharge(s)) {
        var scn = self.surchargePieces(s);
        html += '<p class="bc__hint bc__hint--surcharge" data-surcharge-hint>Sizes marked +' + formatMoney(self.config.sizeSurcharge.priceCents, self.config.moneyFormat) + ' cost a little more per piece, the same as our regular pricing.' +
          (scn ? ' <strong>' + scn + ' ' + (scn === 1 ? 'piece' : 'pieces') + ' · +' + formatMoney(self.config.sizeSurcharge.priceCents * scn, self.config.moneyFormat) + '</strong>' : '') + '</p>';
      }
      html += '</div>';
    } else if (colorOpt) {
      html += '<p class="bc__hint">Select a colour above to set sizes.</p>';
    }

    if (err) html += '<p class="bc__error" role="alert">' + esc(err) + '</p>';

    // add-ons
    if ((s.step.addons || []).length) {
      var pieces = t.assigned || s.step.quantity;
      html += '<div class="bc__section bc__section--addons"><div class="bc__section-head"><h4>Optional upgrades</h4><span>Priced per piece</span></div>';
      s.step.addons.forEach(function (a) {
        var st = g.addons[a.key] || { on: false, mode: 'main' };
        var key = 'addon:' + s.step.key + ':' + a.key;
        var onSale = a.compareAtCents > a.priceCents;
        html += '<div class="bc__addon' + (st.on ? ' is-on' : '') + '">' +
          '<label class="bc__addon-row">' +
          '<span class="bc__addon-text"><strong>' + esc(a.title) + '</strong>' + (a.description ? '<span>' + esc(a.description) + '</span>' : '') + '</span>' +
          '<span class="bc__addon-price"><strong>+' + formatMoney(a.priceCents, self.config.moneyFormat) + '</strong><span>' + (onSale ? '<s>' + formatMoney(a.compareAtCents, self.config.moneyFormat) + '</s> ' : '') + 'per piece</span></span>' +
          '<span class="bc__switch"><input type="checkbox" data-addon-toggle data-step="' + esc(s.step.key) + '" data-addon="' + esc(a.key) + '"' + (st.on ? ' checked' : '') + ' aria-label="Add ' + esc(a.title) + '"><span></span></span>' +
          '</label>';
        if (st.on) {
          html += '<div class="bc__addon-body">' +
            '<p class="bc__addon-total">Applies to all <strong>' + pieces + '</strong> ' + esc(s.label.toLowerCase()) + ' · <strong>+' + formatMoney(a.priceCents * pieces, self.config.moneyFormat) + '</strong></p>' +
            (a.key.indexOf('wax') > -1 ? '' :
              '<div class="bc__radio-row">' +
              '<label class="bc__radio"><input type="radio" name="mode-' + esc(key) + '" value="main" data-addon-mode data-step="' + esc(s.step.key) + '" data-addon="' + esc(a.key) + '"' + (st.mode !== 'custom' ? ' checked' : '') + '><span>Use my main logo</span></label>' +
              '<label class="bc__radio"><input type="radio" name="mode-' + esc(key) + '" value="custom" data-addon-mode data-step="' + esc(s.step.key) + '" data-addon="' + esc(a.key) + '"' + (st.mode === 'custom' ? ' checked' : '') + '><span>Different artwork</span></label>' +
              '</div>' +
              (st.mode === 'custom' ? self.uploadCard(key, st.artwork, { title: 'Upload ' + a.title.toLowerCase() + ' artwork', removable: false }) : '')) +
            '</div>';
        }
        html += '</div>';
      });
      html += '</div>';
    }

    html += '</div>';
    return html;
  };

  Configurator.prototype.viewReview = function () {
    var self = this;
    var tot = this.totals();
    var fmt = this.config.moneyFormat;
    var html = '<div class="bc__step">';
    html += '<h3 class="bc__h3">Everything look right?</h3><p class="bc__lead">You can still edit any step. Your bundle is added to the cart as one item, with any upgrades listed underneath it.</p>';

    // artwork
    var a = this.state.artwork;
    html += '<div class="bc__review-block"><div class="bc__review-head"><h4>Logo</h4><button type="button" class="bc__link" data-act="goto" data-i="0">Edit</button></div>';
    html += a && a.url ? '<div class="bc__review-art">' + (previewFor(a) ? '<img src="' + esc(previewFor(a)) + '" alt="">' : '<span class="bc__upload-ext">' + esc(fileExt(a.name).toUpperCase()) + '</span>') + '<span>' + esc(a.name) + '</span></div>' : '<p class="bc__error">No logo uploaded.</p>';
    html += '</div>';

    // garments
    this.steps.forEach(function (s, i) {
      if (s.type !== 'garment') return;
      var g = self.state.garments[s.step.key];
      var t = self.garmentTotals(s);
      html += '<div class="bc__review-block"><div class="bc__review-head"><h4>' + esc(s.step.quantity + ' ' + s.label) + '</h4><button type="button" class="bc__link" data-act="goto" data-i="' + i + '">Edit</button></div>';
      if (!self.stepComplete(i)) html += '<p class="bc__error">' + esc(self.stepError(i)) + '</p>';
      html += '<ul class="bc__review-list">';
      g.colors.forEach(function (c) {
        var parts = [];
        Object.keys(c.qty).forEach(function (z) {
          var q = Number(c.qty[z]) || 0;
          if (q > 0) parts.push((z === '__default' ? '' : sizeLabel(z) + ' ') + '× ' + q);
        });
        if (!parts.length) return;
        html += '<li>' + (s.colorImages[c.color] ? '<img src="' + esc(s.colorImages[c.color]) + '" alt="">' : '') + '<strong>' + esc(c.color === '__default' ? s.step.product.title : c.color) + '</strong><span>' + esc(parts.join(', ')) + '</span></li>';
      });
      html += '</ul>';
      var on = (s.step.addons || []).filter(function (ad) { return g.addons[ad.key] && g.addons[ad.key].on; });
      if (on.length) {
        html += '<ul class="bc__review-addons">' + on.map(function (ad) {
          var st = g.addons[ad.key];
          var note = ad.key.indexOf('wax') > -1 ? '' : (st.mode === 'custom' ? ' · custom artwork' : ' · main logo');
          return '<li><span>+ ' + esc(ad.title) + ' × ' + t.assigned + note + '</span><strong>+' + formatMoney(ad.priceCents * t.assigned, fmt) + '</strong></li>';
        }).join('') + '</ul>';
      }
      var sl = self.surchargeLine(s);
      if (sl) html += '<ul class="bc__review-addons"><li><span>+ ' + esc(sl.addon.title) + ' × ' + sl.pieces + '</span><strong>+' + formatMoney(sl.total, fmt) + '</strong></li></ul>';
      html += '</div>';
    });

    if (this.config.notesEnabled) {
      html += '<div class="bc__review-block"><div class="bc__review-head"><h4>Notes for our team <span class="bc__optional">optional</span></h4></div>' +
        '<textarea class="bc__textarea" data-notes rows="3" maxlength="800" placeholder="Placement preferences, deadlines, anything we should know.">' + esc(this.state.notes || '') + '</textarea></div>';
    }

    html += '<div class="bc__review-totals"><div><span>' + esc(this.config.product.title) + ' · ' + this.config.totalPieces + ' pieces</span><strong>' + formatMoney(tot.base, fmt) + '</strong></div>';
    tot.addons.forEach(function (l) {
      html += '<div><span>' + esc(l.addon.title) + ' · ' + esc(l.stepLabel) + ' × ' + l.pieces + '</span><strong>+' + formatMoney(l.total, fmt) + '</strong></div>';
    });
    html += '<div class="bc__review-grand"><span>Total</span><strong>' + formatMoney(tot.total, fmt) + '</strong></div>' +
      '<p class="bc__fine">Taxes and shipping are calculated at checkout.</p></div>';

    if (this.cartError) html += '<p class="bc__error bc__error--big" role="alert">' + esc(this.cartError) + '</p>';
    html += '</div>';
    return html;
  };

  Configurator.prototype.renderSummary = function () {
    var self = this;
    var tot = this.totals();
    var fmt = this.config.moneyFormat;
    var html = '<div class="bc__sum">';
    html += '<div class="bc__sum-head">' + (this.config.product.image ? '<img src="' + esc(this.config.product.image) + '" alt="">' : '') + '<div><strong>' + esc(this.config.product.title) + '</strong><span>' + this.config.totalPieces + ' pieces</span></div></div>';
    html += '<ul class="bc__sum-list">';
    this.steps.forEach(function (s, i) {
      if (s.type === 'review') return;
      var done = self.stepComplete(i);
      var label, detail = '';
      if (s.type === 'artwork') { label = 'Logo'; detail = self.state.artwork ? self.state.artwork.name : 'Not uploaded'; }
      else {
        var t = self.garmentTotals(s);
        label = s.step.quantity + ' ' + s.label;
        detail = t.assigned + ' of ' + t.required + ' assigned';
        var g = self.state.garments[s.step.key];
        var on = (s.step.addons || []).filter(function (a) { return g.addons[a.key] && g.addons[a.key].on; }).map(function (a) { return a.title; });
        if (on.length) detail += ' · ' + on.join(', ');
      }
      html += '<li class="' + (done ? 'is-done' : '') + (i === self.state.current ? ' is-current' : '') + '"><button type="button" data-act="goto" data-i="' + i + '"' + (i > (self.state.maxVisited || 0) + 1 ? ' disabled' : '') + '><span class="bc__sum-dot"></span><span class="bc__sum-text"><strong>' + esc(label) + '</strong><span>' + esc(detail) + '</span></span></button></li>';
    });
    html += '</ul>';
    html += '<div class="bc__sum-totals"><div><span>Bundle</span><strong>' + formatMoney(tot.base, fmt) + '</strong></div>';
    tot.addons.forEach(function (l) {
      html += '<div><span>' + esc(l.addon.title) + ' × ' + l.pieces + '</span><strong>+' + formatMoney(l.total, fmt) + '</strong></div>';
    });
    html += '<div class="bc__sum-grand"><span>Total</span><strong data-total>' + formatMoney(tot.total, fmt) + '</strong></div></div>';
    html += '</div>';
    this.els.summary.innerHTML = html;
  };

  Configurator.prototype.renderFooter = function () {
    var cur = this.state.current;
    var last = cur === this.steps.length - 1;
    var tot = this.totals();
    var html = '<div class="bc__foot">';
    html += '<button type="button" class="bc__btn bc__btn--ghost" data-act="back"' + (cur === 0 ? ' disabled' : '') + '>Back</button>';
    html += '<div class="bc__foot-total"><span>Total</span><strong data-total>' + formatMoney(tot.total, this.config.moneyFormat) + '</strong></div>';
    if (last) {
      html += '<button type="button" class="bc__btn bc__btn--primary" data-act="add-to-cart"' + (this.busy || !this.allComplete() ? ' disabled' : '') + '>' + (this.busy ? 'Adding…' : 'Add bundle to cart') + '</button>';
    } else {
      html += '<button type="button" class="bc__btn bc__btn--primary" data-act="next">' + (cur === this.steps.length - 2 ? 'Review' : 'Next') + '</button>';
    }
    html += '</div>';
    this.els.footer.innerHTML = html;
  };

  // Light refresh used while typing quantities (keeps focus in inputs)
  Configurator.prototype.refreshLive = function () {
    var s = this.steps[this.state.current];
    if (s.type === 'garment') {
      var t = this.garmentTotals(s);
      var counter = this.els.body.querySelector('[data-counter]');
      if (counter) {
        counter.className = 'bc__counter ' + (t.assigned === t.required ? 'is-complete' : (t.assigned > t.required ? 'is-over' : ''));
        counter.innerHTML = '<strong>' + t.assigned + '</strong><span>of ' + t.required + '</span>';
      }
      var head = this.els.body.querySelectorAll('.bc__section-head span')[s.colorOpt ? 1 : 0];
      if (head) head.textContent = t.remaining ? t.remaining + ' left to assign' : (t.assigned > t.required ? (t.assigned - t.required) + ' over' : 'All assigned');
      var g = this.state.garments[s.step.key];
      var self = this;
      this.els.body.querySelectorAll('[data-color-block]').forEach(function (blk) {
        var c = g.colors.filter(function (x) { return x.color === blk.getAttribute('data-color-block'); })[0];
        if (!c) return;
        var sub = 0; Object.keys(c.qty).forEach(function (k) { sub += Number(c.qty[k]) || 0; });
        var el = blk.querySelector('[data-color-sub]');
        if (el) el.textContent = sub + ' ' + (sub === 1 ? 'piece' : 'pieces');
      });
      this.els.body.querySelectorAll('[data-act="qty"]').forEach(function (b) {
        var input = b.parentNode.querySelector('input');
        var q = Number(input.value) || 0;
        if (b.getAttribute('data-delta') === '1') b.disabled = q >= s.step.quantity;
        else b.disabled = q <= 0;
      });
      this.els.body.querySelectorAll('.bc__addon-total strong:first-child').forEach(function (el) { el.textContent = t.assigned || s.step.quantity; });
      // Recompute add-on totals text
      (s.step.addons || []).forEach(function (a) {
        var st = g.addons[a.key];
        if (!st || !st.on) return;
        var pieces = t.assigned || s.step.quantity;
        var el = self.els.body.querySelector('[data-addon-toggle][data-addon="' + a.key + '"]');
        var body = el && el.closest('.bc__addon').querySelector('.bc__addon-total');
        if (body) body.innerHTML = 'Applies to all <strong>' + pieces + '</strong> ' + esc(s.label.toLowerCase()) + ' · <strong>+' + formatMoney(a.priceCents * pieces, self.config.moneyFormat) + '</strong>';
      });
      var hint = this.els.body.querySelector('[data-surcharge-hint]');
      if (hint && this.config.sizeSurcharge) {
        var scn = this.surchargePieces(s);
        var sc = this.config.sizeSurcharge;
        hint.innerHTML = 'Sizes marked +' + formatMoney(sc.priceCents, this.config.moneyFormat) + ' cost a little more per piece, the same as our regular pricing.' +
          (scn ? ' <strong>' + scn + ' ' + (scn === 1 ? 'piece' : 'pieces') + ' · +' + formatMoney(sc.priceCents * scn, this.config.moneyFormat) + '</strong>' : '');
      }
      var errEl = this.els.body.querySelector('.bc__step > .bc__error');
      if (errEl && t.assigned === t.required) { errEl.remove(); delete this.errors[this.state.current]; }
    }
    this.renderSummary();
    this.renderHeader();
    var footTotal = this.els.footer.querySelector('[data-total]');
    if (footTotal) footTotal.textContent = formatMoney(this.totals().total, this.config.moneyFormat);
    this.persist();
  };

  /* ---------------------------------------------------------------- events */

  Configurator.prototype.onAction = function (el, e) {
    var act = el.getAttribute('data-act');
    var s = this.steps[this.state.current];
    switch (act) {
      case 'goto': this.goTo(Number(el.getAttribute('data-i'))); break;
      case 'next': this.goTo(this.state.current + 1); break;
      case 'back': this.goTo(this.state.current - 1, { force: true }); break;
      case 'toggle-color': this.toggleColor(s, el.getAttribute('data-color')); break;
      case 'remove-color': this.removeColor(s, el.getAttribute('data-color')); break;
      case 'qty': this.bumpQty(s, el.getAttribute('data-color'), el.getAttribute('data-size'), Number(el.getAttribute('data-delta'))); break;
      case 'replace-upload': {
        var input = this.els.body.querySelector('[data-upload-input="' + el.getAttribute('data-key') + '"]');
        if (input) input.click();
        break;
      }
      case 'remove-upload': this.setUpload(el.getAttribute('data-key'), null); this.renderAll(); break;
      case 'add-to-cart': this.addToCart(); break;
    }
  };

  Configurator.prototype.onInput = function (e) {
    var t = e.target;
    if (t.matches('[data-qty]')) {
      var s = this.steps[this.state.current];
      var v = Math.max(0, Math.floor(Number(t.value) || 0));
      this.setQty(s, t.getAttribute('data-color'), t.getAttribute('data-size'), v, t);
    } else if (t.matches('[data-notes]')) {
      this.state.notes = t.value;
      this.persist();
    }
  };

  Configurator.prototype.onChange = function (e) {
    var t = e.target;
    var s = this.steps[this.state.current];
    if (t.matches('[data-upload-input]')) {
      var f = t.files && t.files[0];
      if (f) this.handleUpload(t.getAttribute('data-upload-input'), f);
      t.value = '';
    } else if (t.matches('[data-addon-toggle]')) {
      var g = this.state.garments[t.getAttribute('data-step')];
      var a = g.addons[t.getAttribute('data-addon')];
      a.on = t.checked;
      this.persist();
      this.renderAll();
    } else if (t.matches('[data-addon-mode]')) {
      var g2 = this.state.garments[t.getAttribute('data-step')];
      var a2 = g2.addons[t.getAttribute('data-addon')];
      a2.mode = t.value;
      this.persist();
      this.renderAll();
    } else if (t.matches('[data-qty]')) {
      // normalise the input display after typing
      t.value = this.getQty(s, t.getAttribute('data-color'), t.getAttribute('data-size'));
    }
  };

  Configurator.prototype.colorEntry = function (s, color, create) {
    var g = this.state.garments[s.step.key];
    var c = g.colors.filter(function (x) { return x.color === color; })[0];
    if (!c && create) { c = { color: color, qty: {} }; g.colors.push(c); }
    return c;
  };

  Configurator.prototype.toggleColor = function (s, color) {
    var g = this.state.garments[s.step.key];
    var existing = this.colorEntry(s, color, false);
    if (existing) {
      g.colors = g.colors.filter(function (x) { return x !== existing; });
    } else {
      var entry = this.colorEntry(s, color, true);
      // Convenience: single-size products (caps) get the remaining quantity pre-filled
      var sizeKeys = s.sizeOpt
        ? s.sizeOpt.values.filter(function (z) { var v = s.variantIndex[color + '||' + z]; return v && v.available; })
        : ['__default'];
      if (sizeKeys.length === 1) {
        var t = this.garmentTotals(s);
        if (t.remaining > 0) entry.qty[sizeKeys[0]] = t.remaining;
      }
    }
    delete this.errors[this.state.current];
    this.persist();
    this.renderAll();
    // Scroll the new block into view
    if (!existing) {
      var blk = this.els.body.querySelector('[data-color-block="' + CSS.escape(color) + '"]');
      if (blk) blk.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  Configurator.prototype.removeColor = function (s, color) {
    var g = this.state.garments[s.step.key];
    g.colors = g.colors.filter(function (x) { return x.color !== color; });
    this.persist();
    this.renderAll();
  };

  Configurator.prototype.getQty = function (s, color, size) {
    var c = this.colorEntry(s, color, false);
    return c ? (Number(c.qty[size]) || 0) : 0;
  };

  Configurator.prototype.setQty = function (s, color, size, value, inputEl) {
    var c = this.colorEntry(s, color, true);
    // One size is capped at the step total. Going over the step total ACROSS sizes is
    // allowed on purpose: the counter turns red and the step error says how many to
    // remove. The old code clamped to "remaining", which silently rewrote a number the
    // customer had just typed back down to 0 once the other sizes were full. That looked
    // like the quantity dropping on its own, and it hit the 2XL+ rows hardest because
    // those are the ones people fill last.
    var v = Math.min(Math.max(0, value), s.step.quantity);
    c.qty[size] = v;
    if (inputEl && document.activeElement !== inputEl && Number(inputEl.value) !== v) inputEl.value = v;
    this.refreshLive();
  };

  Configurator.prototype.bumpQty = function (s, color, size, delta) {
    var cur = this.getQty(s, color, size);
    this.setQty(s, color, size, cur + delta);
    var input = this.els.body.querySelector('[data-qty][data-color="' + CSS.escape(color) + '"][data-size="' + CSS.escape(size) + '"]');
    if (input) input.value = this.getQty(s, color, size);
  };

  Configurator.prototype.setUpload = function (key, upload) {
    if (key === 'main') { this.state.artwork = upload; }
    else {
      var p = key.split(':'); // addon:stepKey:addonKey
      var g = this.state.garments[p[1]];
      if (g && g.addons[p[2]]) g.addons[p[2]].artwork = upload;
    }
    this.persist();
  };

  Configurator.prototype.handleUpload = function (key, file) {
    var self = this;
    this.uploadErrors = this.uploadErrors || {};
    delete this.uploadErrors[key];
    var ext = fileExt(file.name);
    var maxBytes = (this.config.upload.maxMb || 25) * 1024 * 1024;
    if (ALLOWED_EXT.indexOf(ext) === -1) {
      this.uploadErrors[key] = 'That file type is not supported. Use PNG, JPG, PDF, SVG, AI, EPS, PSD or TIFF.';
      this.renderBody(); return;
    }
    if (file.size > maxBytes) {
      this.uploadErrors[key] = 'That file is larger than ' + (this.config.upload.maxMb || 25) + ' MB. Please compress it or send it to us after ordering.';
      this.renderBody(); return;
    }
    this.uploading[key] = 0;
    this.renderBody();
    uploadToUploadcare(file, this.config.upload, function (p) {
      self.uploading[key] = p;
      var bar = self.els.body.querySelector('.bc__upload.is-busy span');
      var txt = self.els.body.querySelector('.bc__upload.is-busy p');
      if (bar) bar.style.width = Math.round(p * 100) + '%';
      if (txt) txt.textContent = 'Uploading… ' + Math.round(p * 100) + '%';
    }).then(function (res) {
      delete self.uploading[key];
      self.setUpload(key, res);
      delete self.errors[self.state.current];
      self.renderAll();
    }).catch(function (err) {
      delete self.uploading[key];
      self.uploadErrors[key] = err.message || 'Upload failed. Please try again.';
      self.renderBody();
    });
  };

  /* ------------------------------------------------------------ add to cart */

  Configurator.prototype.buildLines = function () {
    var self = this;
    var st = this.state;
    var id = st.bundleId;
    var tot = this.totals();
    var props = {};
    var summary = { bundleId: id, logo: st.artwork ? { name: st.artwork.name, url: st.artwork.url } : null, items: [], addons: [], notes: st.notes || '' };

    props['Logo'] = st.artwork.url;

    this.steps.forEach(function (s) {
      if (s.type !== 'garment') return;
      var g = st.garments[s.step.key];
      var parts = [];
      var item = { step: s.step.key, title: s.step.product.title, quantity: s.step.quantity, lines: [] };
      g.colors.forEach(function (c) {
        Object.keys(c.qty).forEach(function (z) {
          var q = Number(c.qty[z]) || 0;
          if (q <= 0) return;
          var v = s.variantIndex[c.color + '||' + z];
          var label = (c.color === '__default' ? '' : c.color) + (z === '__default' ? '' : (c.color === '__default' ? '' : ' / ') + z);
          parts.push((label || 'Default') + ' × ' + q);
          item.lines.push({ color: c.color === '__default' ? null : c.color, size: z === '__default' ? null : z, quantity: q, variantId: v ? v.id : null, sku: v ? v.title : null });
        });
      });
      // Label lines by the bundle step name ("20 T-Shirts"), not the catalog product title ("Men's Crewnecks")
      props[s.step.quantity + ' ' + s.label] = parts.join(', ');
      summary.items.push(item);
    });

    if (st.notes) props['Notes'] = st.notes;
    props['_bundle_id'] = id;
    props['_bundle_role'] = 'parent';
    props['_bundle_total_cents'] = String(tot.total);

    var addonLines = [];
    tot.addons.forEach(function (l) {
      var artwork = l.artwork ? l.artwork.url : 'Same as main logo';
      var isWax = l.addon.key && l.addon.key.indexOf('wax') > -1;
      var lp = {};
      if (l.surcharge) {
        lp['Size surcharge'] = 'Sizes priced above the garment base price (2XL and up)';
        lp['For'] = l.pieces + ' of the ' + l.stepLabel + ' · ' + self.config.product.title;
      } else if (isWax) {
        lp['Finish'] = l.addon.title;
        lp['For'] = l.pieces + ' ' + l.stepLabel + ' · ' + self.config.product.title;
      } else {
        lp['Print location'] = l.addon.title;
        lp['For'] = l.pieces + ' ' + l.stepLabel + ' · ' + self.config.product.title;
        lp['Artwork'] = artwork;
      }
      lp['_bundle_id'] = id;
      lp['_bundle_role'] = 'addon';
      lp['_bundle_step'] = l.stepKey;
      addonLines.push({ id: l.addon.variantId, quantity: l.pieces, properties: lp });
      summary.addons.push({ step: l.stepKey, title: l.addon.title, pieces: l.pieces, unitCents: l.addon.priceCents, totalCents: l.total, artwork: l.artwork ? l.artwork.url : null, surcharge: !!l.surcharge });
    });

    props['_Bundle Summary'] = JSON.stringify(summary);

    var items = [{ id: this.config.product.variantId, quantity: 1, properties: props }].concat(addonLines);
    return { items: items, expectedCents: tot.total, lineCount: items.length, bundleId: id };
  };

  Configurator.prototype.addToCart = function () {
    var self = this;
    if (this.busy) return;
    this.cartError = '';
    if (!this.allComplete()) { this.renderAll(); return; }
    var payload = this.buildLines();
    this.busy = true;
    this.renderFooter();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: payload.items })
    }).then(function (r) {
      return r.json().then(function (data) { if (!r.ok) throw new Error(data.description || data.message || 'Could not add to cart.'); return data; });
    }).then(function () {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
    }).then(function (cart) {
      var group = cart.items.filter(function (i) { return i.properties && i.properties._bundle_id === payload.bundleId; });
      var actual = group.reduce(function (sum, i) { return sum + i.line_price; }, 0);
      if (group.length !== payload.lineCount || actual !== payload.expectedCents) {
        // Roll back this group so the customer is never charged something different from what they saw
        var updates = {};
        group.forEach(function (i) { updates[i.key] = 0; });
        return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: updates }) })
          .then(function () { throw new Error('The cart total did not match your bundle total, so we removed it to be safe. Please try again or contact us.'); });
      }
      return cart;
    }).then(function (cart) {
      self.busy = false;
      self.clearSaved();
      self.state = self.freshState();
      self.close();
      self.afterAdd(cart);
    }).catch(function (err) {
      self.busy = false;
      self.cartError = err.message || 'Something went wrong. Please try again.';
      self.renderAll();
      self.focusError();
    });
  };

  Configurator.prototype.afterAdd = function (cart) {
    // Refresh the theme's cart drawer + header bubble, then open the drawer.
    var drawer = document.querySelector('cart-drawer.cus-cart-drawer') || document.querySelector('cart-drawer');
    var jobs = [];
    jobs.push(fetch('/cart?section_id=cus-cart-drawer').then(function (r) { return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('.drawer__inner.cus-cart-drawer__inner') || doc.querySelector('.drawer__inner');
      var current = drawer && (drawer.querySelector('.drawer__inner.cus-cart-drawer__inner') || drawer.querySelector('.drawer__inner'));
      if (fresh && current) current.innerHTML = fresh.innerHTML;
      if (drawer) drawer.classList.remove('is-empty');
    }).catch(function () {}));
    jobs.push(fetch('/?section_id=cart-icon-bubble').then(function (r) { return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('.shopify-section');
      var bubble = document.getElementById('cart-icon-bubble');
      if (fresh && bubble) bubble.innerHTML = fresh.innerHTML;
    }).catch(function () {}));
    Promise.all(jobs).then(function () {
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-configurator', cartData: cart }); } catch (e) { /* noop */ }
      }
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = '/cart';
    });
  };

  /* ------------------------------------------------------------------ boot */

  function init() {
    document.querySelectorAll('[data-bundle-configurator]').forEach(function (root) {
      if (root.__bc) return;
      var sid = root.getAttribute('data-section-id');
      var cfgEl = document.getElementById('bundle-config-' + sid);
      if (!cfgEl) return;
      var config;
      try { config = JSON.parse(cfgEl.textContent); } catch (e) { console.error('[bundle] bad config JSON', e); return; }
      if (!config.addonProduct || !config.addonProduct.available) {
        // Not fatal: the bundle can still be sold without upgrades, but staff should know.
        console.warn('[bundle] Add-on product missing or unavailable; paid upgrades will not be offered.');
        config.steps.forEach(function (s) { s.addons = []; });
        config.sizeSurcharge = null;
      }
      var conf = new Configurator(root, config);
      root.__bc = conf;
      document.querySelectorAll('[data-bundle-open]').forEach(function (btn) {
        btn.addEventListener('click', function () { conf.open(btn); });
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
